import React, { useEffect, useState } from 'react';
import { GlucoseTargets, GlucoseThresholds } from '../../types/domain';
import { DEFAULT_GLUCOSE_THRESHOLDS } from '../../lib/domain-data';

interface GlucoseTargetEditorProps {
  /** Current values, or null when this patient has no override yet. */
  targets: GlucoseTargets | null;
  /** Shown when `targets` is null, so the editor starts from what applies now. */
  inheritedFrom?: GlucoseTargets | null;
  busy?: boolean;
  error?: string | null;
  onSave: (pre: GlucoseThresholds, post: GlucoseThresholds) => void;
  /** Omitted for the global row, which cannot be cleared. */
  onClear?: () => void;
  clearLabel?: string;
}

type Draft = Record<keyof GlucoseThresholds, string>;

const toDraft = (t: GlucoseThresholds): Draft => ({
  low: String(t.low),
  normalMax: String(t.normalMax),
  warningMax: String(t.warningMax)
});

const parseDraft = (d: Draft): GlucoseThresholds => ({
  low: Number(d.low),
  normalMax: Number(d.normalMax),
  warningMax: Number(d.warningMax)
});

const isAscending = (t: GlucoseThresholds) =>
  Number.isInteger(t.low) &&
  Number.isInteger(t.normalMax) &&
  Number.isInteger(t.warningMax) &&
  t.low < t.normalMax &&
  t.normalMax < t.warningMax;

/**
 * Editor for one pre/post pair of target ranges (FR-06).
 *
 * Numbers only. The four clinician-approved message strings are stored with
 * the row but never surfaced here.
 */
export const GlucoseTargetEditor: React.FC<GlucoseTargetEditorProps> = ({
  targets,
  inheritedFrom,
  busy = false,
  error,
  onSave,
  onClear,
  clearLabel = 'Kembalikan ke default'
}) => {
  const base = targets ?? inheritedFrom;
  const [pre, setPre] = useState<Draft>(toDraft(base?.pre ?? DEFAULT_GLUCOSE_THRESHOLDS));
  const [post, setPost] = useState<Draft>(toDraft(base?.post ?? DEFAULT_GLUCOSE_THRESHOLDS));

  useEffect(() => {
    const next = targets ?? inheritedFrom;
    setPre(toDraft(next?.pre ?? DEFAULT_GLUCOSE_THRESHOLDS));
    setPost(toDraft(next?.post ?? DEFAULT_GLUCOSE_THRESHOLDS));
  }, [targets, inheritedFrom]);

  const parsedPre = parseDraft(pre);
  const parsedPost = parseDraft(post);
  const valid = isAscending(parsedPre) && isAscending(parsedPost);

  const renderRow = (
    label: string,
    hint: string,
    draft: Draft,
    setDraft: (d: Draft) => void,
    idPrefix: string
  ) => {
    const parsed = parseDraft(draft);
    const ok = isAscending(parsed);
    return (
      <div className="target-band">
        <div className="target-band-head">
          <span className="target-band-label">{label}</span>
          <span className="target-band-hint">{hint}</span>
        </div>
        <div className="target-band-inputs">
          {(
            [
              ['low', 'Batas rendah'],
              ['normalMax', 'Batas aman'],
              ['warningMax', 'Batas waspada']
            ] as [keyof GlucoseThresholds, string][]
          ).map(([field, fieldLabel]) => (
            <label key={field} className="target-field" htmlFor={`${idPrefix}-${field}`}>
              <span>{fieldLabel}</span>
              <input
                id={`${idPrefix}-${field}`}
                type="number"
                inputMode="numeric"
                min={20}
                max={600}
                value={draft[field]}
                disabled={busy}
                onChange={e => setDraft({ ...draft, [field]: e.target.value })}
              />
            </label>
          ))}
        </div>
        {!ok ? (
          <div className="target-band-error">
            Urutan harus menaik: batas rendah &lt; batas aman &lt; batas waspada.
          </div>
        ) : (
          <div className="target-band-preview">
            &lt; {parsed.low} rendah · {parsed.low}–{parsed.normalMax} aman ·{' '}
            {parsed.normalMax + 1}–{parsed.warningMax} waspada · &gt; {parsed.warningMax} tinggi
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="target-editor">
      {targets === null && inheritedFrom ? (
        <div className="target-inherited">
          Saat ini mengikuti target umum. Menyimpan akan membuat target khusus untuk
          pasien ini.
        </div>
      ) : null}

      {renderRow(
        'Pra-makan',
        'Dipakai juga untuk sebelum tidur, tengah malam, saat hipoglikemia dan sewaktu.',
        pre,
        setPre,
        'pre'
      )}
      {renderRow(
        'Pasca-makan',
        'Dipakai untuk Post sarapan, Post makan siang dan Post makan malam.',
        post,
        setPost,
        'post'
      )}

      {error ? <div className="target-band-error">{error}</div> : null}

      <div className="target-actions">
        <button
          type="button"
          className="btn-primary"
          disabled={busy || !valid}
          onClick={() => onSave(parsedPre, parsedPost)}
        >
          {busy ? 'Menyimpan...' : 'Simpan target'}
        </button>
        {onClear && targets !== null ? (
          <button type="button" className="btn-secondary" disabled={busy} onClick={onClear}>
            {clearLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default GlucoseTargetEditor;
